import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Assignment,
  Timer,
  Flag,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import assessmentService, { AssessmentDetail } from '../services/assessmentService';
import { toast } from 'react-toastify';

const ViewAssessment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessment, setAssessment] = useState<AssessmentDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAssessment();
  }, [id]);

  const loadAssessment = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await assessmentService.getAssessmentById(parseInt(id));
      setAssessment(data);
    } catch (error) {
      console.error('Error loading assessment:', error);
      toast.error('Failed to load assessment');
      navigate('/assessments');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (threshold: string) => {
    if (threshold.includes('Red')) return 'error';
    if (threshold.includes('Yellow')) return 'warning';
    if (threshold.includes('Green')) return 'success';
    return 'default';
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!assessment) {
    return (
      <Container>
        <Typography>Assessment not found</Typography>
      </Container>
    );
  }

  const isAdmin = user?.userType === 'ROLE_ADMIN';
  const isProvider = user?.userType === 'ROLE_PROVIDER';
  const canEdit = isAdmin || isProvider;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/assessments')}
          >
            Back to Assessments
          </Button>
          <Typography variant="h4" component="h1">
            {assessment.title}
          </Typography>
        </Box>
        {canEdit && (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/assessments/${id}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              startIcon={<Assignment />}
              onClick={() => navigate(`/assessments/${id}/assign`)}
            >
              Assign
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Assessment Details
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {assessment.description}
              </Typography>
              
              <Box display="flex" gap={2} mb={2}>
                <Chip
                  label={assessment.category}
                  color="primary"
                  size="small"
                />
                <Chip
                  icon={<Timer />}
                  label={`${assessment.estimatedMinutes} minutes`}
                  size="small"
                />
                <Chip
                  label={assessment.isActive ? 'Active' : 'Inactive'}
                  color={assessment.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Instructions:
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {assessment.instructions}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Questions ({assessment.totalQuestions})
              </Typography>
              
              <List>
                {assessment.questions.map((question, index) => (
                  <ListItem key={question.id} divider>
                    <ListItemText
                      primary={`${index + 1}. ${question.questionText}`}
                      secondary={
                        <Box mt={1}>
                          <Typography variant="caption" display="block">
                            Type: {question.questionType} | 
                            Required: {question.isRequired ? 'Yes' : 'No'} |
                            Points: {question.pointsIfApplicable || 'N/A'}
                          </Typography>
                          {question.options.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary">
                                Options:
                              </Typography>
                              <List dense>
                                {question.options.map((option) => (
                                  <ListItem key={option.id} sx={{ py: 0 }}>
                                    <ListItemText
                                      primary={option.optionText}
                                      secondary={`Points: ${option.pointValue}${
                                        option.flagIfSelected ? ' (Flagged)' : ''
                                      }`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Risk Thresholds
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Flag color="error" />
                <Typography>
                  High Risk: ≥ {assessment.redFlagThreshold} points
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Flag color="warning" />
                <Typography>
                  Medium Risk: ≥ {assessment.yellowFlagThreshold} points
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Flag color="success" />
                <Typography>
                  Low Risk: ≥ {assessment.greenFlagThreshold} points
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Metadata
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2">
                <strong>Created:</strong> {new Date(assessment.createdAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Updated:</strong> {new Date(assessment.updatedAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Total Questions:</strong> {assessment.totalQuestions}
              </Typography>
              <Typography variant="body2">
                <strong>Category:</strong> {assessment.category}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ViewAssessment;